import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelection } from '@/hooks/useSelection';
import BatchActionsBar from '@/components/BatchActionsBar';
import PaginationControls from '@/components/PaginationControls';
import { usePagination } from '@/hooks/usePagination';
import {
  Appointment,
  useAdvanceAppointment,
  useAppointmentReminder,
  useAppointments,
} from '@/services/api';
import useMockDataToast from '@/hooks/useMockDataToast';
import { useAuth } from '@/state/AuthContext';
import { useToasts } from '@/state/ToastContext';
import { useSearchParams } from 'react-router-dom';

const STATUS_FILTERS = ['all', 'check-in', 'pre-op', 'follow-up', 'completed'] as const;
const STATUS_FILTER_LABELS: Record<(typeof STATUS_FILTERS)[number], string> = {
  all: '全部狀態',
  'check-in': '報到',
  'pre-op': '術前',
  'follow-up': '追蹤',
  completed: '已完成',
};
const STATUS_DISPLAY: Record<string, string> = {
  'check-in': '報到',
  'pre-op': '術前',
  'in progress': '處置中',
  recovery: '復原',
  completed: '已完成',
  scheduled: '已排程',
  'follow-up': '追蹤',
};
const formatStatusLabel = (value?: string | null) => {
  if (!value) return STATUS_DISPLAY.scheduled;
  return STATUS_DISPLAY[value.toLowerCase()] ?? value;
};

const TIME_FILTERS = [
  { id: 'all', label: '整天' },
  { id: 'morning', label: '上午' },
  { id: 'afternoon', label: '下午' },
  { id: 'evening', label: '晚上' },
] as const;

const providerSchedule = [
  { name: 'Dr. Avery', block: '08:00 - 14:00', focus: '內科', load: '5/6 案件' },
  { name: 'Dr. Ramos', block: '10:00 - 18:00', focus: '外科', load: '4 台手術' },
  { name: 'Dr. Patel', block: '12:00 - 20:00', focus: 'ICU 主責', load: '隨時待命' },
];

const reminderQueue = [
  { label: '術前禁食提醒', status: '已送出 12 則', owner: '自動化' },
  { label: '化驗結果完成', status: '4 筆待簽核', owner: '檢驗室' },
  { label: '飼主接回時間', status: '6 人待回覆', owner: '櫃檯' },
];

const STATUS_PIPELINE = ['Check-in', 'Pre-op', 'In progress', 'Recovery', 'Completed'];

const deriveNextStatus = (current?: string | null) => {
  if (!current) return STATUS_PIPELINE[0];
  const index = STATUS_PIPELINE.findIndex(
    (status) => status.toLowerCase() === current.toLowerCase(),
  );
  if (index === -1 || index === STATUS_PIPELINE.length - 1) {
    return STATUS_PIPELINE[STATUS_PIPELINE.length - 1];
  }
  return STATUS_PIPELINE[index + 1];
};

const Appointments: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: appointments = [],
    isLoading,
    isFallback,
    fallbackMessage,
  } = useAppointments();
  useMockDataToast(
    isFallback,
    '預約服務無回應，顯示快取排程。',
    fallbackMessage,
  );
  const advanceAppointment = useAdvanceAppointment();
  const appointmentReminder = useAppointmentReminder();
  const { hasPermission } = useAuth();
  const { pushToast } = useToasts();

  const canAdvanceStatus = hasPermission('schedule:modify');
  const canSendReminder = hasPermission('schedule:view') || hasPermission('schedule:modify');
  const updateSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      const next = new URLSearchParams(searchParams);
      let changed = false;
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          if (next.has(key)) {
            next.delete(key);
            changed = true;
          }
          return;
        }
        if (next.get(key) !== value) {
          next.set(key, value);
          changed = true;
        }
      });
      if (changed) {
        setSearchParams(next, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>('all');
  const [timeFilter, setTimeFilter] = useState<(typeof TIME_FILTERS)[number]['id']>('all');
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [statusNote, setStatusNote] = useState('');
  const [statusError, setStatusError] = useState<string | null>(null);
  const [reminderChannel, setReminderChannel] = useState<'SMS' | 'Email' | 'Portal'>('SMS');
  const [reminderMessage, setReminderMessage] = useState('');
  const [reminderError, setReminderError] = useState<string | null>(null);
  const [statusLogs, setStatusLogs] = useState<Record<string, { status: string; timestamp: string }>>({});
  const [reminderLogs, setReminderLogs] = useState<
    Record<string, { channel: string; message?: string; timestamp: string }>
  >({});
  const selectAppointment = useCallback(
    (appointment: Appointment | null, options?: { updateUrl?: boolean }) => {
      setSelectedAppointment(appointment);
      if (options?.updateUrl === false) return;
      updateSearchParams({ selected: appointment ? String(appointment.id) : null });
    },
    [updateSearchParams],
  );
  const handleSearchChange = (value: string) => {
    setSearch(value);
    updateSearchParams({ q: value });
  };

  const filteredAppointments = useMemo(() => {
    return appointments
      .filter((appt) => {
        const statusMatch =
          statusFilter === 'all' ||
          (appt.status ?? 'scheduled').toLowerCase() === statusFilter.toLowerCase();
        if (!statusMatch) return false;

        if (timeFilter === 'all') return true;
        const hour = new Date(appt.date).getHours();
        if (timeFilter === 'morning') return hour < 12;
        if (timeFilter === 'afternoon') return hour >= 12 && hour < 17;
        if (timeFilter === 'evening') return hour >= 17;
        return true;
      })
      .filter((appt) => {
        if (!search.trim()) return true;
        const needle = search.toLowerCase();
        return (
          appt.petName.toLowerCase().includes(needle) ||
          (appt.owner ?? '').toLowerCase().includes(needle) ||
          (appt.vetName ?? '').toLowerCase().includes(needle)
        );
      });
  }, [appointments, statusFilter, timeFilter, search]);

  useEffect(() => {
    const paramQuery = searchParams.get('q') ?? '';
    if (paramQuery !== search) {
      setSearch(paramQuery);
    }
  }, [searchParams, search]);

  const nextStatusLabel = useMemo(() => {
    if (!selectedAppointment) return null;
    return deriveNextStatus(selectedAppointment.status);
  }, [selectedAppointment]);

  // Sorting
  const [apptSort, setApptSort] = useState<{ key: 'date' | 'petName' | 'vetName' | 'type' | 'status'; direction: 'asc' | 'desc' } | null>(null);
  const sortedAppointments = useMemo(() => {
    if (!apptSort) return filteredAppointments;
    const { key, direction } = apptSort;
    const sorted = [...filteredAppointments].sort((a, b) => {
      let aVal: any = key === 'date' ? new Date(a.date).getTime() : (a as any)[key] ?? '';
      let bVal: any = key === 'date' ? new Date(b.date).getTime() : (b as any)[key] ?? '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredAppointments, apptSort]);

  const toggleApptSort = (key: 'date' | 'petName' | 'vetName' | 'type' | 'status') => {
    setApptSort((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  };

  // Pagination
  const apptPage = usePagination(sortedAppointments, { perPage: 25 });
  // Selection for batch operations
  const appointmentSelection = useSelection<Appointment['id']>();
  const visibleApptIds = apptPage.pageItems.map(a => a.id);
  const allVisibleSelected = appointmentSelection.allSelected(visibleApptIds) && visibleApptIds.length > 0;
  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      visibleApptIds.forEach(id => appointmentSelection.toggle(id));
    } else {
      appointmentSelection.selectMany(visibleApptIds);
    }
  };

  useEffect(() => {
    const selectedParam = searchParams.get('selected');
    if (selectedParam) {
      const match = appointments.find((appt) => String(appt.id) === selectedParam);
      if (match && match.id !== selectedAppointment?.id) {
        selectAppointment(match, { updateUrl: false });
        return;
      }
    }
    if (!selectedParam) {
      if (
        selectedAppointment &&
        !filteredAppointments.some((appt) => appt.id === selectedAppointment.id)
      ) {
        selectAppointment(filteredAppointments[0] ?? null, { updateUrl: false });
        return;
      }
      if (!selectedAppointment && filteredAppointments.length > 0) {
        selectAppointment(filteredAppointments[0], { updateUrl: false });
      }
    }
  }, [searchParams, appointments, filteredAppointments, selectedAppointment, selectAppointment]);

  const handleAdvanceStatus = async () => {
    if (!selectedAppointment || !canAdvanceStatus) return;
    setStatusError(null);
    const nextStatus = deriveNextStatus(selectedAppointment.status);
    try {
      const result = await advanceAppointment.mutateAsync({
        appointmentId: selectedAppointment.id,
        status: nextStatus,
        note: statusNote.trim() || undefined,
      });
      setSelectedAppointment((prev) =>
        prev && prev.id === selectedAppointment.id ? { ...prev, status: result.status ?? prev.status } : prev,
      );
      const statusLabel = formatStatusLabel(result.status ?? nextStatus);
      pushToast({
        tone: 'success',
        message: `已將 ${selectedAppointment.petName} 移至 ${statusLabel}`,
      });
      setStatusLogs((prev) => ({
        ...prev,
        [String(selectedAppointment.id)]: {
          status: statusLabel,
          timestamp: new Date().toISOString(),
        },
      }));
      setStatusNote('');
    } catch (error) {
      setStatusError(error instanceof Error ? error.message : '狀態更新失敗，請稍後再試');
      pushToast({
        tone: 'error',
        message: '無法推進狀態',
        detail: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const handleSendReminder = async () => {
    if (!selectedAppointment || !canSendReminder) return;
    setReminderError(null);
    try {
      await appointmentReminder.mutateAsync({
        appointmentId: selectedAppointment.id,
        channel: reminderChannel,
        message: reminderMessage.trim() || undefined,
      });
      pushToast({
        tone: 'success',
        message: `提醒已送給 ${selectedAppointment.owner ?? '飼主'}`,
      });
      setReminderLogs((prev) => ({
        ...prev,
        [String(selectedAppointment.id)]: {
          channel: reminderChannel,
          message: reminderMessage.trim() || undefined,
          timestamp: new Date().toISOString(),
        },
      }));
      setReminderMessage('');
    } catch (error) {
      setReminderError(error instanceof Error ? error.message : '提醒發送失敗，請稍後再試');
      pushToast({
        tone: 'error',
        message: '無法發送提醒',
        detail: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const statusSummary = useMemo(() => {
    const map = new Map<string, number>();
    appointments.forEach((appt) => {
      const status = (appt.status ?? 'Scheduled').toLowerCase();
      map.set(status, (map.get(status) ?? 0) + 1);
    });
    return Array.from(map.entries()).map(([status, count]) => ({
      status,
      count,
    }));
  }, [appointments]);

  useEffect(() => {
    setStatusNote('');
    setStatusError(null);
    setReminderChannel('SMS');
    setReminderMessage('');
    setReminderError(null);
  }, [selectedAppointment?.id]);

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className="section-title">臨床排程</p>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900">預約管理</h1>
          <div className="flex gap-2 sm:gap-3">
            <button className="btn-secondary px-3 sm:px-4 py-2 text-xs sm:text-sm min-h-[44px]" type="button">
              上傳轉診
            </button>
            <button className="btn-primary px-3 sm:px-4 py-2 text-xs sm:text-sm min-h-[44px]" type="button">
              ＋ 建立預約
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter}
                type="button"
                onClick={() => setStatusFilter(filter)}
                className={`rounded-2xl px-3 py-2 text-xs font-semibold capitalize touch-target ${
                  statusFilter === filter
                    ? 'bg-brand-blue text-white'
                    : 'bg-white text-slate-500 shadow-sm'
                }`}
              >
                {STATUS_FILTER_LABELS[filter]}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            {TIME_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                onClick={() => setTimeFilter(filter.id)}
                className={`rounded-2xl px-3 py-2 text-xs font-semibold touch-target ${
                  timeFilter === filter.id
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-500 shadow-sm'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <input
            type="search"
            placeholder="搜尋病患、飼主或醫師"
            value={search}
            onChange={(event) => handleSearchChange(event.target.value)}
            className="min-w-[200px] flex-1 form-input px-3 py-2 text-xs sm:text-sm"
          />
        </div>
      </div>

      <div className="glass-panel overflow-hidden">
        <BatchActionsBar
          count={appointmentSelection.count}
          onClear={appointmentSelection.clear}
          scopeLabel="預約"
          actions={[
            {
              label: '匯出 CSV',
              variant: 'primary',
              onClick: () => {
                const selected = appointments.filter(a => appointmentSelection.isSelected(a.id));
                const header = ['id','petName','vetName','date','status'];
                const rows = selected.map(a => [
                  a.id,
                  a.petName,
                  a.vetName ?? '',
                  new Date(a.date).toISOString(),
                  formatStatusLabel(a.status)
                ]);
                const csv = [header.join(','), ...rows.map(r => r.join(','))].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.download = `appointments-${selected.length}.csv`;
                anchor.click();
                URL.revokeObjectURL(url);
                pushToast({ tone: 'success', message: `已匯出 ${selected.length} 筆預約` });
              }
            },
            {
              label: '指派獸醫',
              onClick: () => {
                pushToast({ tone: 'info', message: '指派獸醫功能即將推出' });
              }
            },
            {
              label: '批次取消',
              variant: 'danger',
              onClick: () => {
                const toCancel = appointments.filter(a => appointmentSelection.isSelected(a.id));
                if (toCancel.length === 0) return;
                if (!window.confirm(`確定要取消 ${toCancel.length} 筆預約？此操作無法復原。`)) return;
                // Placeholder for cancel API
                pushToast({ tone: 'success', message: `已取消 ${toCancel.length} 筆預約 (示範)` });
                appointmentSelection.clear();
              }
            }
          ]}
        />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] table-auto divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-3">
                  <input
                    type="checkbox"
                    aria-label="選取可見的所有預約"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                  />
                </th>
                <th className="px-6 py-3">
                  <button
                    type="button"
                    onClick={() => toggleApptSort('date')}
                    className="flex items-center gap-1 font-semibold transition hover:text-slate-700"
                  >
                    時間
                    {apptSort?.key === 'date' && (
                      <span className="text-[10px] font-normal">{apptSort.direction === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </button>
                </th>
                <th className="px-6 py-3">
                  <button
                    type="button"
                    onClick={() => toggleApptSort('petName')}
                    className="flex items-center gap-1 font-semibold transition hover:text-slate-700"
                  >
                    病患
                    {apptSort?.key === 'petName' && (
                      <span className="text-[10px] font-normal">{apptSort.direction === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </button>
                </th>
                <th className="px-6 py-3">
                  <button
                    type="button"
                    onClick={() => toggleApptSort('vetName')}
                    className="flex items-center gap-1 font-semibold transition hover:text-slate-700"
                  >
                    主治醫師
                    {apptSort?.key === 'vetName' && (
                      <span className="text-[10px] font-normal">{apptSort.direction === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </button>
                </th>
                <th className="px-6 py-3">
                  <button
                    type="button"
                    onClick={() => toggleApptSort('type')}
                    className="flex items-center gap-1 font-semibold transition hover:text-slate-700"
                  >
                    類型
                    {apptSort?.key === 'type' && (
                      <span className="text-[10px] font-normal">{apptSort.direction === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </button>
                </th>
                <th className="px-6 py-3">診間/區域</th>
                <th className="px-6 py-3">
                  <button
                    type="button"
                    onClick={() => toggleApptSort('status')}
                    className="flex items-center gap-1 font-semibold transition hover:text-slate-700"
                  >
                    狀態
                    {apptSort?.key === 'status' && (
                      <span className="text-[10px] font-normal">{apptSort.direction === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                    載入預約中…
                  </td>
                </tr>
              ) : filteredAppointments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                    沒有符合篩選條件的預約。
                  </td>
                </tr>
              ) : (
                apptPage.pageItems.map((appt) => (
                  <tr
                    key={appt.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => selectAppointment(appt)}
                  >
                    <td className="px-3 py-4">
                      <input
                        type="checkbox"
                        aria-label={`選取預約 ${appt.petName}`}
                        checked={appointmentSelection.isSelected(appt.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          appointmentSelection.toggle(appt.id);
                        }}
                        className="h-4 w-4 rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                      />
                    </td>
                    <td className="px-6 py-4 text-slate-500">
                      {new Date(appt.date).toLocaleString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-slate-900">{appt.petName}</p>
                      <p className="text-xs text-slate-500">{appt.owner ?? '尚未指定飼主'}</p>
                    </td>
                    <td className="px-6 py-4">{appt.vetName}</td>
                    <td className="px-6 py-4">
                      <span className="pill bg-indigo-50 text-brand-indigo">
                        {appt.type ?? '一般門診'}
                      </span>
                    </td>
                    <td className="px-6 py-4">{appt.room ?? '處置區 A'}</td>
                    <td className="px-6 py-4">
                      <span className="pill bg-slate-100 text-slate-700">
                        {formatStatusLabel(appt.status)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls
          page={apptPage.page}
          perPage={apptPage.perPage}
          total={apptPage.total}
          totalPages={apptPage.totalPages}
          canNext={apptPage.canNext}
          canPrev={apptPage.canPrev}
          startIndex={apptPage.startIndex}
          endIndex={apptPage.endIndex}
          onPageChange={apptPage.setPage}
          onPerPageChange={apptPage.setPerPage}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="glass-panel space-y-4 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="section-title mb-1">醫療人力</p>
              <h3 className="text-lg font-semibold text-slate-900">醫師值班表</h3>
            </div>
            <button className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:border-brand-blue hover:text-brand-blue">
              匯出到 Slack
            </button>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            {providerSchedule.map((provider) => (
              <div
                key={provider.name}
                className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm"
              >
                <p className="font-semibold text-slate-900">{provider.name}</p>
                <p className="text-xs text-slate-500">{provider.block}</p>
                <p className="mt-2 text-xs text-slate-500">{provider.focus}</p>
                <p className="mt-2 text-xs font-semibold text-brand-blue">{provider.load}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-panel space-y-3 p-6">
          <div>
            <p className="section-title mb-1">自動化佇列</p>
            <h3 className="text-lg font-semibold text-slate-900">訊息狀態</h3>
          </div>
          <ul className="space-y-2 text-sm">
            {reminderQueue.map((item) => (
              <li
                key={item.label}
                className="rounded-2xl border border-slate-100 bg-white px-4 py-3"
              >
                <p className="font-semibold text-slate-900">{item.label}</p>
                <p className="text-xs text-slate-500">{item.owner}</p>
                <p className="text-xs font-semibold text-brand-blue">{item.status}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="glass-panel space-y-3 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="section-title mb-1">狀態分佈</p>
              <h3 className="text-lg font-semibold text-slate-900">即時分級概況</h3>
            </div>
            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">
              {statusSummary.reduce((total, item) => total + item.count, 0)} 筆預約
            </span>
          </div>
          <ul className="space-y-2 text-sm">
            {statusSummary.map((summary) => (
              <li
                key={summary.status}
                className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-2"
              >
                <span>{formatStatusLabel(summary.status)}</span>
                <span className="font-semibold text-slate-900">{summary.count}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="glass-panel space-y-4 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="section-title mb-1">預約詳細</p>
              <h3 className="text-lg font-semibold text-slate-900">
                {selectedAppointment ? selectedAppointment.petName : '請選擇預約'}
              </h3>
              {selectedAppointment && (
                <p className="text-xs text-slate-500">
                  {selectedAppointment.owner ?? '尚未指定飼主'} ｜{' '}
                  {new Date(selectedAppointment.date).toLocaleString()}
                </p>
              )}
            </div>
            {selectedAppointment && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSendReminder}
                  disabled={!canSendReminder || appointmentReminder.isPending}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-brand-blue hover:text-brand-blue disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {appointmentReminder.isPending ? '傳送中…' : '發送提醒'}
                </button>
                <button
                  type="button"
                  onClick={handleAdvanceStatus}
                  disabled={!canAdvanceStatus || advanceAppointment.isPending}
                  className="rounded-xl bg-brand-blue px-3 py-2 text-xs font-semibold text-white shadow-soft transition disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {advanceAppointment.isPending ? '更新中…' : '推進狀態'}
                </button>
              </div>
            )}
          </div>

          {selectedAppointment ? (
            <>
              <div className="space-y-3 rounded-2xl border border-slate-100 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">狀態流程</p>
                    <p className="text-xs text-slate-500">
                      下一步：{nextStatusLabel ? formatStatusLabel(nextStatusLabel) : '—'}
                    </p>
                  </div>
                  {advanceAppointment.isPending && (
                    <span className="rounded-full bg-brand-blue/10 px-3 py-1 text-[11px] font-semibold text-brand-blue">
                      更新中…
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {STATUS_PIPELINE.map((stage) => {
                    const isCurrent =
                      selectedAppointment.status &&
                      stage.toLowerCase() === selectedAppointment.status.toLowerCase();
                    const isNext = nextStatusLabel && stage.toLowerCase() === nextStatusLabel.toLowerCase();
                    const className = isCurrent
                      ? 'bg-brand-blue text-white'
                      : isNext
                        ? 'border border-brand-blue/40 bg-white text-brand-blue'
                        : 'border border-slate-200 bg-slate-50 text-slate-600';
                    return (
                      <span
                        key={stage}
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${className}`}
                      >
                        {stage}
                      </span>
                    );
                  })}
                </div>
                {selectedAppointment && statusLogs[String(selectedAppointment.id)] && (
                  <p className="text-[11px] text-slate-500">
                    上次更新：{' '}
                    {new Date(statusLogs[String(selectedAppointment.id)].timestamp).toLocaleString()} ·{' '}
                    {statusLogs[String(selectedAppointment.id)].status}
                  </p>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    處置類型
                  </p>
                  <p className="mt-2 text-slate-900">
                    {selectedAppointment.type ?? '一般門診'}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    診間 {selectedAppointment.room ?? '待定'}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    主治醫師
                  </p>
                  <p className="mt-2 text-slate-900">{selectedAppointment.vetName}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    狀態：{formatStatusLabel(selectedAppointment.status)}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    待辦事項
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-slate-600">
                    <li>確認禁食與用藥清單</li>
                    <li>將最新化驗附加到病歷</li>
                    <li>通知飼主預計接回時間</li>
                  </ul>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2 rounded-2xl border border-slate-100 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-900">狀態備註</p>
                    <span className="text-xs text-slate-500">送出時一併寫入 API</span>
                  </div>
                  <textarea
                    value={statusNote}
                    onChange={(event) => setStatusNote(event.target.value)}
                    placeholder="輸入此次狀態更新說明（例如：已完成術前評估）"
                    className="min-h-[90px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue/30"
                    maxLength={240}
                  />
                  {statusError && (
                    <p className="text-xs text-rose-600" role="alert">
                      {statusError}
                    </p>
                  )}
                  <p className="text-right text-[11px] text-slate-400">{statusNote.length}/240</p>
                </div>
                <div className="space-y-2 rounded-2xl border border-slate-100 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-900">提醒內容</p>
                    <span className="text-xs text-slate-500">支援 SMS / Email / Portal</span>
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <label className="flex flex-col text-xs font-semibold text-slate-600">
                      通道
                      <select
                        value={reminderChannel}
                        onChange={(event) =>
                          setReminderChannel(event.target.value as 'SMS' | 'Email' | 'Portal')
                        }
                        className="mt-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue/30"
                      >
                        <option value="SMS">SMS</option>
                        <option value="Email">Email</option>
                        <option value="Portal">Owner Portal</option>
                      </select>
                    </label>
                    <label className="flex flex-1 flex-col text-xs font-semibold text-slate-600">
                      自訂訊息
                      <input
                        type="text"
                        value={reminderMessage}
                        onChange={(event) => setReminderMessage(event.target.value)}
                        placeholder="例如：請準備術前禁食 8 小時"
                        className="mt-1 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:border-brand-blue focus:ring-brand-blue/30"
                        maxLength={160}
                      />
                    </label>
                  </div>
                  {reminderError && (
                    <p className="text-xs text-rose-600" role="alert">
                      {reminderError}
                    </p>
                  )}
                  <p className="text-right text-[11px] text-slate-400">{reminderMessage.length}/160</p>
                  {selectedAppointment && reminderLogs[String(selectedAppointment.id)] && (
                    <p className="text-[11px] text-slate-500">
                      最近提醒：{' '}
                      {new Date(reminderLogs[String(selectedAppointment.id)].timestamp).toLocaleString()} ·{' '}
                      {reminderLogs[String(selectedAppointment.id)].channel}
                    </p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-slate-400">
              請先選取左側的預約以檢視詳細資訊與操作。
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Appointments;
